// src/App.js
import React, { useEffect, useMemo } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";

import ScrollToTop from "./components/ScrollToTop";
import TopNav from "./components/TopNav";
import BottomNav from "./components/BottomNav";
import ErrorBoundary from "./components/ErrorBoundary";
import PageLayout from "./components/PageLayout";

import AppRoutes from "./routes";

/** ✅ yyyy-MM-dd (로컬 기준) */
function ymdLocal(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/* ✅ 방문 경로 추적 컴포넌트 */
function VisitorTracker() {
  const location = useLocation();

  useEffect(() => {
    fetch(`/api/visitor-track?path=${encodeURIComponent(location.pathname)}`).catch(
      (err) => console.error("방문 경로 전송 실패:", err)
    );
  }, [location]);

  // 일별 고유 방문자 카운트 (브라우저당 하루 1회, 관리자/디스플레이 화면 제외)
  useEffect(() => {
    const path = location.pathname;
    if (
      path.startsWith("/admin") ||
      path.startsWith("/display") ||
      path.startsWith("/tableorder") ||
      path === "/menu" ||
      path === "/smenu"
    )
      return;
    try {
      const today = ymdLocal();
      if (localStorage.getItem("visitor-last-hit") === today) return;
      let id = localStorage.getItem("visitor-id");
      if (!id) {
        id = window.crypto?.randomUUID?.() ??
          `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
        localStorage.setItem("visitor-id", id);
      }
      fetch(`/api/visitors/hit?visitor_id=${encodeURIComponent(id)}`, { method: "POST" })
        .then((res) => res.ok && localStorage.setItem("visitor-last-hit", today))
        .catch(() => {});
    } catch (e) {
      // localStorage 사용 불가 환경은 카운트하지 않음
    }
  }, [location]);

  return null;
}

/**
 * ✅ 라우트별 PageLayout 적용 래퍼
 * - 기본: PageLayout로 감싼다 (모바일 full / PC 1200 고정 / Navbar fixed 보정 포함)
 * - 홈(/): overlay 유지 위해 layout 제외 가능
 */
function WithLayout({ children }) {
  const location = useLocation();

  // ✅ Admin 여부
  const isAdmin = location.pathname.startsWith("/admin");
  const isHome = location.pathname === "/";

  // ✅ 특정 디스플레이 페이지 + Admin 에서는 Navbar/BottomNav 숨김
  const hideNavbar = useMemo(() => {
    const p = location.pathname;
    return (
      isAdmin ||
      ["/display/menuboard", "/display/specialmenu", "/tableorder", "/menu", "/smenu"].includes(p)
    );
  }, [location.pathname, isAdmin]);

  const variant = useMemo(() => {
    const p = location.pathname;
    if (p === "/" || p.startsWith("/cafe")) return "cafe";
    return "candle";
  }, [location.pathname]);

  return (
    <>
      {/* ✅ 상단 매뉴바 (admin/display 외에는 항상 노출) */}
      {!hideNavbar && <TopNav />}

      {/* ✅ Admin: 완전 흰 배경으로만 */}
      {isAdmin ? (
        <main style={{ background: "#fff", minHeight: "100vh", color: "#111" }}>
          {children}
        </main>
      ) : isHome ? (
        <main className="w-full">{children}</main>
      ) : (
        <PageLayout variant={variant}>{children}</PageLayout>
      )}

      {/* ✅ 하단 매뉴바 (홈/예약/게임/문의) — admin/display 외에는 항상 노출 */}
      {!hideNavbar && <BottomNav />}
    </>
  );
}

function AppContent() {
  useEffect(() => {
    // ✅ 안드로이드 크롬 주소창 움직임으로 인한 vh 변화 방지
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setVh();
    window.addEventListener("resize", setVh);
    return () => window.removeEventListener("resize", setVh);
  }, []);

  const today = ymdLocal(new Date());

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <VisitorTracker />

      <WithLayout>
        <AppRoutes today={today} />
      </WithLayout>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
